// Modal.js
import React, { useState, useRef, useEffect } from "react";
import "../../components/Modal.css";
import { useCreateClientMutation } from "actions/clients/mutations";
// import { useDispatch } from "react-redux";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { useGetCodesClientsQuery } from "actions/comptabilite/queries";
import Switch from "components/SwitchButton/Switch";
import GeneriqueModal from "components/GeneriqueModal";
import ButtonGreen from "components/buttons/ButtonGreen";
import ButtonCancel from "components/buttons/ButtonCancel";
import CustomSelectController from "components/CustomSelect/CustomSelect";
import useGetCurrentUser from "hooks/useGetCurrentUser";

const ModalClient = ({ isOpen, onClose, onDossierCreated, refetch, refetchCodesTiers }) => {
  const { clientID: id } = useParams();
  const currentUser = useGetCurrentUser();
  const { data: codes = [] } = useGetCodesClientsQuery();
  const [creationClient, { isLoading }] = useCreateClientMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const isMountedRef = useRef(true);


  
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setError,
    reset,
  } = useFormContext();

  const [lettrage, setLettrage] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Déterminer si l'utilisateur est une entreprise
  const isEntreprise = currentUser?.status === "ENTREPRISE";

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      // Réinitialiser l'état quand le modal se ferme
      if (reset) {
        try {
          reset({
            nom: '',
            telephone: '',
            adresse: '',
            email: '',
            compte_comptable: null,
          });
        } catch (error) {
          // Ignorer les erreurs de réinitialisation
        }
      }
      setLettrage(false);
      setApiError(null);
    }
  }, [isOpen, reset]);

  const handleClose = () => {
    // Fermer proprement le modal
    onClose();
  };

  const getErrorMessage = (error) => {
    if (!error) return "Une erreur est survenue";
    
    if (error.data && typeof error.data === 'object') {
      if (error.data.detail) return error.data.detail;
      
      const firstKey = Object.keys(error.data)[0];
      if (firstKey && Array.isArray(error.data[firstKey])) {
        return error.data[firstKey][0];
      }
      if (firstKey && typeof error.data[firstKey] === 'string') {
        return error.data[firstKey];
      }
    }
    
    return error.message || "Une erreur est survenue";
  };

  const onSubmit = async (data) => {
    if (!isMountedRef.current || isSubmittingRef.current || isLoading || isSubmitting) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setApiError(null);
    
    try {
      // Vérifier que le compte_comptable est valide
      let compteComptableValue = null;
      if (data.compte_comptable) {
        if (typeof data.compte_comptable === 'object' && data.compte_comptable.value !== undefined) {
          // Extraire la valeur (peut être un nombre ou une chaîne)
          const rawValue = data.compte_comptable.value;
          if (typeof rawValue === 'number') {
            compteComptableValue = rawValue;
          } else {
            const strValue = String(rawValue).trim();
            // Convertir en nombre si c'est un nombre valide, sinon garder comme string
            const numValue = Number(strValue);
            compteComptableValue = !isNaN(numValue) && strValue !== '' ? numValue : strValue;
          }
        } else if (typeof data.compte_comptable === 'string' && data.compte_comptable.trim()) {
          const strValue = data.compte_comptable.trim();
          const numValue = Number(strValue);
          // Convertir en nombre si possible, sinon garder comme string
          compteComptableValue = !isNaN(numValue) ? numValue : strValue;
        } else if (typeof data.compte_comptable === 'number') {
          compteComptableValue = data.compte_comptable;
        }
      }

      if (compteComptableValue === null || compteComptableValue === undefined || 
          (typeof compteComptableValue === 'string' && compteComptableValue.trim() === '')) {
        const fieldLabel = isEntreprise ? 'Type' : 'Compte comptable';
        setError('compte_comptable', {
          type: 'manual',
          message: `${fieldLabel} obligatoire`
        });
        toast.error(`Veuillez sélectionner ou créer un ${fieldLabel.toLowerCase()}`);
        return;
      }

      // Nettoyer les champs : le backend Django attend des chaînes même vides pour telephone, email, adresse
      const cleanEmail = data.email && data.email.trim() ? data.email.trim() : '';
      const cleanTelephone = data.telephone && data.telephone.trim() ? data.telephone.trim() : '';
      const cleanAdresse = data.adresse && data.adresse.trim() ? data.adresse.trim() : '';

      // Convertir dossier_id en nombre si c'est une string (PrimaryKeyRelatedField attend un nombre)
      const dossierIdNum = typeof id === 'string' ? parseInt(id, 10) : id;
      
      // S'assurer que compte_comptable est une string (le backend fait .isdigit() dessus)
      const compteComptableStr = typeof compteComptableValue === 'number' 
        ? compteComptableValue.toString() 
        : String(compteComptableValue);
      
      const clientData = {
        nom: data.nom?.trim() || '',
        dossier_id: dossierIdNum,
        lettrage: lettrage,
        compte_comptable: compteComptableStr,
        email: cleanEmail,
        telephone: cleanTelephone,
        adresse: cleanAdresse,
      };
      
      if (!isMountedRef.current) return;

      await creationClient(clientData).unwrap();
      
      if (!isMountedRef.current) return;

      // Réinitialiser le formulaire après succès
      if (reset) {
        reset({
          nom: '',
          telephone: '',
          adresse: '',
          email: '',
          compte_comptable: null,
        });
      }
      setLettrage(false);
      setApiError(null);

      // Rafraîchir les données
      if (refetch) {
        refetch();
      }
      if (refetchCodesTiers) {
        refetchCodesTiers();
      }
      
      toast.success("Création du client réussie !");
      onDossierCreated();
    } catch (error) {
      if (!isMountedRef.current) return;
      
      const errorMessage = getErrorMessage(error);
      setApiError(errorMessage);
      
      if (error?.data && typeof error.data === 'object') {
        Object.keys(error.data).forEach((fieldName) => {
          const fieldError = error.data[fieldName];
          if (Array.isArray(fieldError)) {
            setError(fieldName, {
              type: "server",
              message: fieldError[0]
            });
          }
        });
      }
      
      toast.error(errorMessage);
    } finally {
      isSubmittingRef.current = false;
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };


  // Adapter les options en fonction du statut de l'utilisateur
  const options = codes?.map((code_client) => ({
    value: code_client.code,
    label: isEntreprise ? code_client.libelle : code_client.combine,
  }));

  return (
    <>
      <GeneriqueModal
        className="modal-overlay"
        onClose={handleClose}
        isOpen={isOpen}
        title="Ajouter un client">
        <form
          className="fournisseur-form"
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <label htmlFor="nom" className="form-label">
              Prénom et nom <span style={{ color: "red" }}>(*)</span>
            </label>
            <input
              className="form-controll"
              type="text"
              id="nom"
              placeholder="Prénom et nom"
              {...register("nom", { required: "Nom obligatoire" })}
            />
            {errors.nom && (
              <div className="text-danger">{errors.nom.message}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="telephone" className="form-label">
              Numéro de téléphone
            </label>
            <input
              className="form-controll"
              type="tel"
              id="telephone"
              name="telephone"
              placeholder="777777777 (optionnel)"
              {...register("telephone", { 
                maxLength: {
                  value: 9,
                  message: "Le numéro de téléphone ne doit pas dépasser 9 caractères"
                },
                validate: (value) => {
                  if (!value || value.trim() === '') return true; // Champ optionnel
                  if (value.length > 9) return "Le numéro de téléphone ne doit pas dépasser 9 caractères";
                  return true;
                }
              })}
            />
            {errors.telephone && (
              <div className="text-danger">{errors.telephone.message}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="adresse" className="form-label">
              Adresse<span style={{ color: "red" }}>(*)</span>
            </label>
            <input
              className="form-controll"
              type="text"
              id="adresse"
              name="adresse"
              placeholder="Adresse"
              {...register("adresse")}
            />
            {errors.adresse && (
              <div className="text-danger">{errors.adresse.message}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              E-mail
            </label>
            <input
              className="form-controll"
              type="email"
              id="email"
              name="email"
              placeholder="email@example.com (optionnel)"
              {...register("email", {
                validate: (value) => {
                  if (!value || value.trim() === '') return true; // Champ optionnel
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  return emailRegex.test(value) || "Format d'email invalide";
                }
              })}
            />
            {errors.email && (
              <div className="text-danger">{errors.email.message}</div>
            )}
          </div>

          {/* Compte Comptable ou Type selon le statut */}
          <div className="form-group">
            <label htmlFor="compte_comptable" className="form-label">
              {isEntreprise ? "Type" : "Compte Comptable"}<span style={{ color: "red" }}>(*)</span>
            </label>
            <CustomSelectController
              control={control}
              name="compte_comptable"
              rules={{ required: isEntreprise ? "Type obligatoire" : "Compte comptable obligatoire" }}
              options={options || []}
              placeholder={isEntreprise ? "Sélectionner un type" : "Sélectionner un compte comptable"}
              isClearable
              formatCreateLabel={(inputValue) => `Créer "${inputValue}"`}
              onCreateOption={(inputValue) => {
                return { value: inputValue, label: inputValue };
              }}
            />
            {errors.compte_comptable && (
              <div className="text-danger">
                {errors.compte_comptable.message}
              </div>
            )}
          </div>

          {/* Lettrage Checkbox - Caché pour les entreprises */}
          {!isEntreprise && (
            <div className="form-group d-flex align-items-end gap-3">
              <label htmlFor="lettrage" className="form-label">
                Lettrage
              </label>
              <Switch
                isOn={lettrage}
                handleToggle={() => setLettrage(!lettrage)}
                colorOne="#068f7a"
                colorTwo="#ccc"
              />
            </div>
          )}

          {apiError && (
            <div
              style={{
                padding: "12px",
                marginBottom: "16px",
                backgroundColor: "#ffebee",
                border: "1px solid #ef5350",
                borderRadius: "4px",
                color: "#c62828",
                fontSize: "14px",
              }}
            >
              {apiError}
            </div>
          )}

          <div className="modal-footer">
            <ButtonCancel text={"Annuler"} onClick={onClose} />
            <ButtonGreen
              text={isLoading || isSubmitting ? "Enregistrement..." : "Enregistrer"}
              type={"submit"}
              loading={isLoading || isSubmitting}
              disabled={isLoading || isSubmitting}
            />
          </div>

        </form>
      </GeneriqueModal>
    </>
  );
};

export default ModalClient;